// Service d'authentification pour gérer les tokens et les requêtes
import { setToken, removeToken, fetchToken, isTokenValid, getUserFromToken } from '../utils/auth'
import dexieService from './dexieService'
import { VUE_APP_API_URL } from '../utils/env'

class AuthService {
  constructor() {
    this.refreshTokenPromise = null
    this.isRefreshing = false
  }

  /**
   * Connexion avec username/password
   */
  async login(username, password) {
    const response = await fetch(`${VUE_APP_API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erreur de connexion')
    }

    const data = await response.json()
    
    // Stocker les tokens
    await setToken(data.token)
    await dexieService.setRefreshToken(data.refresh_token)
    await dexieService.setUser(data.user)

    return data
  }

  /**
   * Déconnexion
   */
  async logout() {
    try {
      const token = await fetchToken()
      if (token) {
        // Appeler l'endpoint de logout pour invalider les tokens côté serveur
        await fetch(`${VUE_APP_API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })
      }
    } catch (error) {
      console.warn('Erreur lors de la déconnexion côté serveur:', error)
    } finally {
      // Nettoyer le stockage local dans tous les cas
      await removeToken()
      await dexieService.removeRefreshToken()
      await dexieService.removeUser()
    }
  }

  /**
   * Rafraîchir le token
   */
  async refreshToken() {
    // Éviter les appels multiples simultanés
    if (this.isRefreshing) {
      return this.refreshTokenPromise
    }

    this.isRefreshing = true
    
    this.refreshTokenPromise = this._performRefresh()
    
    try {
      const result = await this.refreshTokenPromise
      return result
    } finally {
      this.isRefreshing = false
      this.refreshTokenPromise = null
    }
  }

  async _performRefresh() {
    const refreshToken = await dexieService.getRefreshToken()
    
    if (!refreshToken) {
      throw new Error('Aucun refresh token disponible')
    }

    try {
      const response = await fetch(`${VUE_APP_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Erreur lors du rafraîchissement du token')
      }

      const data = await response.json()
      
      // Mettre à jour les tokens
      await setToken(data.token)
      await dexieService.setRefreshToken(data.refresh_token)
      // Ne mettre à jour l'utilisateur en cache que si le refresh retourne un objet user.
      // Certains endpoints de refresh ne retournent que le token (pas de 'user'),
      // et écraser le cache avec undefined supprimerait l'identité de l'utilisateur,
      // empêchant la PWA de retrouver son ID lors de la création d'une visite.
      if (data.user) {
        await dexieService.setUser(data.user)
      }

      return data
    } catch (error) {
      // En cas d'erreur, nettoyer les tokens et forcer la reconnexion
      await this.logout()
      throw error
    }
  }

  /**
   * Vérifier si l'utilisateur est authentifié
   */
  async isAuthenticated() {
    const token = await fetchToken()
    return token && isTokenValid(token)
  }

  /**
   * Obtenir l'utilisateur actuel depuis le token
   */
  async getCurrentUser() {
    const token = await fetchToken()
    if (token && isTokenValid(token)) {
      return getUserFromToken(token)
    }
    
    // Fallback sur IndexedDB
    const user = await dexieService.getUser()
    return user || null
  }

  /**
   * Obtenir les informations utilisateur depuis l'API
   */
  async fetchCurrentUser() {
    try {
      const response = await this.makeAuthenticatedRequest(`${VUE_APP_API_URL}/auth/me`)
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `API Error: ${response.status} ${response.statusText}`)
      }
      
      const data = await response.json()
      await dexieService.setUser(data.user)
      return data.user
    } catch (error) {
      console.error('Erreur lors de la récupération des informations utilisateur:', error)
      throw error
    }
  }

  /**
   * Intercepteur pour les requêtes API
   * Ajoute automatiquement le token et gère le refresh
   */
  async makeAuthenticatedRequest(url, options = {}) {
    let token = await fetchToken()
    
    // Vérifier si le token est valide
    if (!token || !isTokenValid(token)) {
      try {
        await this.refreshToken()
        token = await fetchToken()
      } catch (error) {
        // Si le refresh échoue, rediriger vers login
        throw new Error('Session expirée, veuillez vous reconnecter')
      }
    }

    // Ajouter le token aux headers
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }

    const config = {
      ...options,
      headers
    }

    const response = await fetch(url, config)
    
    // Si 401, essayer de rafraîchir le token une fois
    if (response.status === 401 && !options._isRetry) {
      try {
        await this.refreshToken()
        const newToken = await fetchToken()
        
        return this.makeAuthenticatedRequest(url, {
          ...options,
          _isRetry: true,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${newToken}`
          }
        })
      } catch (refreshError) {
        await this.logout()
        throw new Error('Session expirée, veuillez vous reconnecter')
      }
    }
    
    return response
  }

  /**
   * Initialiser l'authentification au démarrage de l'app
   */
  async initializeAuth() {
    const token = await fetchToken()
    
    if (token && isTokenValid(token)) {
      // Token valide, vérifier si on a un utilisateur en cache
      const cachedUser = await dexieService.getUser()
      if (cachedUser) {
        // On a un token valide et un utilisateur en cache, pas besoin de faire une requête
        return true
      }
      
      // Pas d'utilisateur en cache, faire une seule requête pour le récupérer
      try {
        await this.fetchCurrentUser()
        return true
      } catch (error) {
        // Vérifier si c'est une erreur réseau (API inaccessible) ou une erreur d'authentification
        const isNetworkError = error.message.includes('Failed to fetch') || 
                              error.message.includes('NetworkError') ||
                              error.message.includes('ERR_CONNECTION') ||
                              !error.message.includes('401') && !error.message.includes('403')
        
        if (isNetworkError) {
          // Si c'est une erreur réseau mais qu'on a le token, continuer
          console.warn('API inaccessible mais token valide')
          return true
        }
        
        // Si erreur d'authentification, essayer de rafraîchir le token
        try {
          await this.refreshToken()
          // Après refresh, l'utilisateur est automatiquement mis en cache
          return true
        } catch (refreshError) {
          // Vérifier si c'est aussi une erreur réseau
          const isRefreshNetworkError = refreshError.message.includes('Failed to fetch') || 
                                       refreshError.message.includes('NetworkError') ||
                                       refreshError.message.includes('ERR_CONNECTION') ||
                                       !refreshError.message.includes('401') && !refreshError.message.includes('403')
          
          if (isRefreshNetworkError) {
            // Si c'est une erreur réseau mais qu'on a le token, continuer
            console.warn('API inaccessible lors du refresh mais token valide')
            return true
          }
          
          // Seulement si c'est une vraie erreur d'authentification, déconnecter
          await this.logout()
          return false
        }
      }
    } else if (await dexieService.getRefreshToken()) {
      // Si pas de token valide mais refresh token disponible
      try {
        await this.refreshToken()
        // Après refresh, l'utilisateur est automatiquement mis en cache
        return true
      } catch (error) {
        // Vérifier si c'est une erreur réseau
        const isNetworkError = error.message.includes('Failed to fetch') || 
                              error.message.includes('NetworkError') ||
                              error.message.includes('ERR_CONNECTION')
        
        if (isNetworkError) {
          // Si erreur réseau et qu'on a un utilisateur en cache, rester connecté
          const cachedUser = await dexieService.getUser()
          if (cachedUser) {
            console.warn('API inaccessible, utilisation des données en cache')
            return true
          }
        }
        
        await this.logout()
        return false
      }
    }
    
    return false
  }
}

export default new AuthService()
